import { Injectable, inject } from '@angular/core';
import { concatMap, firstValueFrom, tap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { WorkspacesService } from '@bizzdesign/data-access-workspaces';
import { TemplateType } from '@bizzdesign/shared/models';
import { MarkdownValue } from '@bizzdesign/shared/dom-models';
import { type WorkspaceCreate } from '@bizzdesign/ai-assistant/shared';
import { generateUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { UserActivityTrackingService } from '@bizzdesign/shared/services';
import { WorkspaceFactoryService } from '@bizzdesign/workspaces/feature-common';
import { onboardingFramePayload } from '../payloads/onboarding-frame-payload';
import { onboardingFramePreview } from '../payloads/onboarding-frame-preview';

@Injectable({ providedIn: 'root' })
export class CreateOnboardingWorkspaceService {
  private readonly workspacesService = inject(WorkspacesService);
  private readonly workspacesFactoryService = inject(WorkspaceFactoryService);
  private readonly translocoService = inject(TranslocoService);
  private readonly userActivityTrackingService = inject(
    UserActivityTrackingService,
  );

  async create(): Promise<void> {
    const workspaceName = await firstValueFrom(
      this.translocoService.selectTranslate(
        'onboarding.workspaceName',
        {},
        'featureWorkspaces',
      ),
    );
    const frameName = await firstValueFrom(
      this.translocoService.selectTranslate(
        'onboarding.frameName',
        {},
        'featureWorkspaces',
      ),
    );
    const frameIntent = await firstValueFrom(
      this.translocoService.selectTranslate(
        'onboarding.frameIntent',
        {},
        'featureWorkspaces',
      ),
    );

    const onboardingObjective = await this.createOnboardingObjective();

    const workspaceCreate: WorkspaceCreate = {
      name: workspaceName,
      objective: onboardingObjective,
      frameCreates: [
        {
          name: frameName,
          intent: frameIntent,
          diagramPayload: onboardingFramePayload,
          templateType: TemplateType.DESIGN,
          frameType: 'design',
          preview: onboardingFramePreview,
        },
      ],
    };

    const workspaceBase = this.workspacesFactoryService.createWorkspaceBase(
      generateUUID(),
      workspaceCreate.name,
    );

    await firstValueFrom(
      this.workspacesFactoryService
        .createWorkspaceForWorkspaceCreate(workspaceBase, workspaceCreate)
        .pipe(
          concatMap((workspace) =>
            this.workspacesService.createWorkspace(workspace),
          ),
          tap((workspace) => {
            this.userActivityTrackingService.updateWorkspaceActivity(
              workspace.id,
            );
          }),
        ),
    );
  }

  private async createOnboardingObjective(): Promise<MarkdownValue> {
    const objective = await firstValueFrom(
      this.translocoService.selectTranslateObject(
        'onboarding.objective',
        {},
        'featureWorkspaces',
      ),
    );

    return new MarkdownValue([
      { insert: `${objective.intro}\n\n` },
      { insert: objective.goalLabel, attributes: { bold: true } },
      { insert: ` ${objective.goalText}\n\n` },
      { insert: objective.scopeLabel, attributes: { bold: true } },
      { insert: '\n' },
      { insert: objective.scopeItem1 },
      { insert: '\n', attributes: { list: 'bullet' } },
      { insert: objective.scopeItem2 },
      { insert: '\n', attributes: { list: 'bullet' } },
      { insert: objective.scopeItem3 },
      { insert: '\n', attributes: { list: 'bullet' } },
    ]);
  }
}
